import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Brain, Clock, CheckCircle, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

const UnifiedEmailManagement = () => {
  const { isDarkMode } = useAdminTheme();
  const [isFetching, setIsFetching] = useState(false);
  const [fetchProgress, setFetchProgress] = useState({
    isFetching: false,
    progress: '',
    logs: []
  });
  const [lastFetch, setLastFetch] = useState(null);
  const [fetchResult, setFetchResult] = useState(null);
  const [aiStats, setAiStats] = useState({
    extracted: 0,
    promoted: 0,
    rejected: 0
  });
  const [aiStatsLoading, setAiStatsLoading] = useState(false);

  const handleFetchEmails = async () => {
    setIsFetching(true);
    setFetchProgress({
      isFetching: true,
      progress: 'Connecting to Gmail...',
      logs: ['🔄 Initializing structured email fetch process...']
    });
    
    try {
      // Add initial progress updates
      setFetchProgress(prev => ({
        ...prev,
        progress: 'Connecting to Gmail...',
        logs: [...prev.logs, '🔗 Connecting to Gmail server...']
      }));
      
      setFetchProgress(prev => ({
        ...prev,
        progress: 'Fetching emails...',
        logs: [...prev.logs, '📧 Fetching emails from primary account (andromeda2108977@gmail.com)...']
      }));
      
      // Add progress updates with timeouts
      setTimeout(() => {
        if (fetchProgress.isFetching) {
          setFetchProgress(prev => ({
            ...prev,
            progress: 'Processing emails (this may take a moment)...',
            logs: [...prev.logs, '⏳ Processing emails (this may take a moment)...']
          }));
        }
      }, 3000);
      
      setTimeout(() => {
        if (fetchProgress.isFetching) {
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, '🔄 Still processing... Please wait...']
          }));
        }
      }, 10000);
      
      setTimeout(() => {
        if (fetchProgress.isFetching) {
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, '⏳ Processing continues in background...']
          }));
        }
      }, 20000);
      
      // Start the fetch process with a timeout
      const fetchPromise = api.post('/email/fetch', { days: 2 }, { timeout: 120000 }); // 2 minute timeout
      
      const response = await fetchPromise;
      
      if (response.data.success) {
        const result = response.data; // Remove .data since response structure is flat
        
        setFetchProgress(prev => ({
          ...prev,
          progress: 'Complete!',
          logs: [...prev.logs, '✅ Email processing completed successfully!']
        }));
        
        // Create detailed status message
        let statusMessage = `✅ Processed ${result.processed} emails`;
        
        if (result.created > 0) {
          statusMessage += ` • ${result.created} new bookings created`;
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, `📝 Created ${result.created} new booking records`]
          }));
        }
        
        if (result.skipped > 0) {
          statusMessage += ` • ${result.skipped} duplicates skipped`;
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, `⏭️ Skipped ${result.skipped} duplicate emails`]
          }));
        }
        
        if (result.errors && result.errors.length > 0) {
          statusMessage += ` • ${result.errors.length} parsing issues`;
          setFetchProgress(prev => ({
            ...prev,
            logs: [...prev.logs, `⚠️ ${result.errors.length} emails had parsing issues`]
          }));
        }
        
        setFetchProgress(prev => ({
          ...prev,
          progress: statusMessage
        }));
        
        setFetchResult(result);
        setLastFetch(new Date());
        toast.success('Emails fetched and processed successfully!');
        
        // Clear progress message after 8 seconds
        setTimeout(() => {
          setFetchProgress({ isFetching: false, progress: '', logs: [] });
        }, 8000);
        
      } else {
        setFetchProgress(prev => ({
          ...prev,
          progress: '❌ Email processing failed',
          logs: [...prev.logs, '❌ Email processing failed']
        }));
        throw new Error(response.data.message || 'Failed to fetch emails');
      }
      
    } catch (error) {
      console.error('Error fetching emails:', error);
      
      // Check if it's a timeout error
      if (error.message.includes('timeout') || error.code === 'ECONNABORTED') {
        setFetchProgress(prev => ({
          ...prev,
          progress: 'Processing in background...',
          logs: [...prev.logs, '⏳ Request timed out, but processing continues in background', '🔄 You will be notified when complete']
        }));
        
        toast.info('Email processing started - you will be notified when complete');
        
        // Clear progress message after 8 seconds
        setTimeout(() => {
          setFetchProgress({ isFetching: false, progress: '', logs: [] });
        }, 8000);
        
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch emails';
        setFetchProgress(prev => ({
          ...prev,
          progress: `❌ Error: ${errorMessage}`,
          logs: [...prev.logs, `❌ Error: ${errorMessage}`]
        }));
        
        toast.error('Failed to fetch emails: ' + errorMessage);
        
        // Clear error message after 8 seconds
        setTimeout(() => {
          setFetchProgress({ isFetching: false, progress: '', logs: [] });
        }, 8000);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const fetchAiStats = async () => {
    try {
      setAiStatsLoading(true);
      const response = await api.get('/enhanced-email/stats');
      if (response.data.success) {
        const stats = response.data.stats;
        setAiStats({
          extracted: stats.stagingRecords?.total || 0,
          promoted: stats.stagingRecords?.promoted || 0,
          rejected: stats.stagingRecords?.rejected || 0
        });
      }
    } catch (error) {
      console.error('Error fetching AI stats:', error);
      // Don't show error toast for stats, just log it
    } finally {
      setAiStatsLoading(false);
    }
  };

  useEffect(() => {
    fetchAiStats();
  }, []);

  const handleTestConnection = async () => {
    try {
      const response = await api.post('/email/test-connection');
      if (response.data.success) {
        toast.success('Gmail connection is working');
      } else {
        toast.error('Gmail connection failed');
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Failed to test Gmail connection');
    }
  };

  const cardShell = isDarkMode
    ? 'rounded-xl border border-skyone-border-dark bg-skyone-inset-dark'
    : 'rounded-xl border border-gray-200 bg-gray-50';

  return (
    <div className={`rounded-lg shadow-sm border transition-colors duration-300 ${
      isDarkMode
        ? 'bg-skyone-card-dark border-skyone-border-dark'
        : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div className={`px-6 py-4 border-b transition-colors duration-300 ${
        isDarkMode ? 'border-skyone-border-dark' : 'border-gray-200'
      }`}>
        <h2 className={`text-lg font-semibold transition-colors duration-300 ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`}>Email Management</h2>
        <p className={`text-sm mt-1 transition-colors duration-300 ${
          isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
        }`}>
          Automatically process emails and create payment requests
        </p>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Structured mails card */}
          <div className={`${cardShell} p-5 flex flex-col min-w-0`}>
            <div className="mb-4">
              <div className="flex items-start gap-3">
                <Mail
                  className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                    isDarkMode ? 'text-accent-400' : 'text-accent-600'
                  }`}
                />
                <div className="min-w-0">
                  <h3
                    className={`text-base font-semibold ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Fetch Structured Mails
                  </h3>
                  <p
                    className={`text-sm mt-0.5 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                    }`}
                  >
                    Process booking emails with predefined templates
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6 flex-1 flex flex-col">
            {/* Connection Status */}
            <div className={`p-4 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark/80 border border-skyone-border-dark' : 'bg-white border border-gray-200'
            }`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center min-w-0">
                  <Mail className={`w-5 h-5 mr-3 flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode ? 'text-accent-400' : 'text-accent-600'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium transition-colors duration-300 ${
                      isDarkMode ? 'text-zinc-100' : 'text-gray-900'
                    }`}>Structured Mail Account</p>
                    <p className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                    }`}>andromeda2108977@gmail.com</p>
                  </div>
                </div>
                <button
                  onClick={handleTestConnection}
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode
                      ? 'text-accent-400 hover:text-accent-500'
                      : 'text-accent-600 hover:text-accent-500'
                  }`}
                >
                  Test Connection
                </button>
              </div>
            </div>

            {/* Fetch Actions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-zinc-100' : 'text-gray-900'
                  }`}>Fetch Recent Emails</h3>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                  }`}>Process emails from the last 2 days</p>
                </div>
                <button
                  onClick={handleFetchEmails}
                  disabled={isFetching}
                  className="btn btn-primary flex items-center"
                >
                  {isFetching ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Fetch Recent Emails
                    </>
                  )}
                </button>
              </div>

              {/* Progress Indicator */}
              {fetchProgress.isFetching && (
                <div className={`p-4 rounded-lg border transition-colors duration-300 ${
                  isDarkMode ? 'bg-accent-500/10 border border-accent-500/35' : 'bg-lime-50 border border-lime-200'
                }`}>
                  <div className={`flex items-center text-sm mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-accent-400' : 'text-lime-900'
                  }`}>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {fetchProgress.progress}
                  </div>
                  
                  {/* Progress Logs */}
                  {fetchProgress.logs && fetchProgress.logs.length > 0 && (
                    <div className="mt-3 space-y-1">
                      <div className={`text-xs font-medium transition-colors duration-300 ${
                        isDarkMode ? 'text-accent-400' : 'text-lime-800'
                      }`}>Progress Log:</div>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {fetchProgress.logs.map((log, index) => (
                          <div key={index} className={`text-xs font-mono transition-colors duration-300 ${
                            isDarkMode ? 'text-accent-400' : 'text-lime-800'
                          }`}>
                            {log}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Success/Error Message */}
              {!fetchProgress.isFetching && fetchProgress.progress && (
                <div className={`p-3 rounded-lg border transition-colors duration-300 ${
                  fetchProgress.progress.includes('✅') 
                    ? (isDarkMode ? 'bg-green-900/20 border-green-700 text-green-300' : 'bg-green-50 border-green-200 text-green-700')
                    : fetchProgress.progress.includes('❌')
                    ? (isDarkMode ? 'bg-red-900/20 border-red-700 text-red-300' : 'bg-red-50 border-red-200 text-red-700')
                    : (isDarkMode ? 'bg-accent-500/10 border border-accent-500/35 text-accent-400' : 'bg-lime-50 border border-lime-200 text-lime-900')
                }`}>
                  <div className="text-sm">
                    {fetchProgress.progress}
                  </div>
                </div>
              )}

              {/* Last Fetch Info */}
              {lastFetch && (
                <div className={`p-3 rounded-lg transition-colors duration-300 ${
                  isDarkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50'
                }`}>
                  <div className={`flex items-center text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-green-300' : 'text-green-700'
                  }`}>
                    <Clock className="w-4 h-4 mr-2" />
                    Last fetched: {lastFetch.toLocaleString()}
                  </div>
                </div>
              )}

              {/* Fetch Results */}
              {fetchResult && (
                <div className="space-y-3">
                  <h4 className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-zinc-100' : 'text-gray-900'
                  }`}>Processing Results</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`text-center p-3 rounded-lg transition-colors duration-300 ${
                      isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
                    }`}>
                      <div className={`text-2xl font-bold transition-colors duration-300 ${
                        isDarkMode ? 'text-zinc-100' : 'text-gray-900'
                      }`}>{fetchResult.processed}</div>
                      <div className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                      }`}>Emails Processed</div>
                    </div>
                    <div className={`text-center p-3 rounded-lg transition-colors duration-300 ${
                      isDarkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50'
                    }`}>
                      <div className={`text-2xl font-bold transition-colors duration-300 ${
                        isDarkMode ? 'text-green-300' : 'text-green-600'
                      }`}>{fetchResult.created}</div>
                      <div className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                      }`}>New Bookings</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`text-center p-3 rounded-lg transition-colors duration-300 ${
                      isDarkMode ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50'
                    }`}>
                      <div className={`text-2xl font-bold transition-colors duration-300 ${
                        isDarkMode ? 'text-yellow-300' : 'text-yellow-600'
                      }`}>{fetchResult.skipped}</div>
                      <div className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                      }`}>Duplicates Skipped</div>
                    </div>
                    <div className={`text-center p-3 rounded-lg transition-colors duration-300 ${
                      isDarkMode ? 'bg-red-900/20 border border-red-700' : 'bg-red-50'
                    }`}>
                      <div className={`text-2xl font-bold transition-colors duration-300 ${
                        isDarkMode ? 'text-red-300' : 'text-red-600'
                      }`}>{fetchResult.errors ? fetchResult.errors.length : 0}</div>
                      <div className={`text-xs transition-colors duration-300 ${
                        isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                      }`}>Parsing Issues</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* What Gets Processed */}
            <div className={`p-4 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-accent-500/10 border border-accent-500/35' : 'bg-lime-50 border border-lime-200'
            }`}>
              <h4 className={`text-sm font-medium mb-3 transition-colors duration-300 ${
                isDarkMode ? 'text-zinc-100' : 'text-lime-950'
              }`}>What gets processed:</h4>
              <ul className={`space-y-2 text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-zinc-300' : 'text-gray-700'
              }`}>
                <li className="flex items-center">
                  <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode ? 'text-green-400' : 'text-green-600'
                  }`} />
                  "New booking" emails → Pending payment requests
                </li>
                <li className="flex items-center">
                  <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode ? 'text-green-400' : 'text-green-600'
                  }`} />
                  "New order" emails → Paid payment requests
                </li>
                <li className="flex items-center">
                  <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode ? 'text-green-400' : 'text-green-600'
                  }`} />
                  Automatic Stripe payment matching for paid orders
                </li>
              </ul>
            </div>
            </div>
          </div>

          {/* Unstructured / AI mails card */}
          <div className={`${cardShell} p-5 flex flex-col min-w-0`}>
            <div className="mb-4">
              <div className="flex items-start gap-3">
                <Brain
                  className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                    isDarkMode ? 'text-accent-400' : 'text-accent-600'
                  }`}
                />
                <div className="min-w-0">
                  <h3
                    className={`text-base font-semibold ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    Fetch Unstructured Mails
                  </h3>
                  <p
                    className={`text-sm mt-0.5 ${
                      isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                    }`}
                  >
                    AI-powered unstructured email parsing
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6 flex-1 flex flex-col">
            {/* Connection Status */}
            <div className={`p-4 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-skyone-card-dark/80 border border-skyone-border-dark' : 'bg-white border border-gray-200'
            }`}>
              <div className="flex items-center">
                <Brain className={`w-5 h-5 mr-3 transition-colors duration-300 ${
                  isDarkMode ? 'text-accent-400' : 'text-accent-600'
                }`} />
                <div>
                  <p className={`text-sm font-medium transition-colors duration-300 ${
                    isDarkMode ? 'text-zinc-100' : 'text-gray-900'
                  }`}>Unstructured Mail Account</p>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                  }`}>mercury210897@gmail.com</p>
                </div>
              </div>
            </div>

            {/* AI Processing Overview */}
            <div className={`p-4 rounded-lg transition-colors duration-300 ${
              isDarkMode ? 'bg-purple-900/20 border border-purple-700' : 'bg-purple-50'
            }`}>
              <div className="flex items-center mb-3">
                <Brain className={`w-5 h-5 mr-2 transition-colors duration-300 ${
                  isDarkMode ? 'text-purple-400' : 'text-purple-600'
                }`} />
                <h3 className={`text-sm font-medium transition-colors duration-300 ${
                  isDarkMode ? 'text-zinc-100' : 'text-gray-900'
                }`}>AI-Powered Email Processing</h3>
              </div>
              <p className={`text-sm mb-4 transition-colors duration-300 ${
                isDarkMode ? 'text-zinc-300' : 'text-gray-700'
              }`}>
                Advanced AI system that can parse unstructured emails and extract booking information automatically.
              </p>
              <div className={`space-y-2 text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-zinc-300' : 'text-gray-700'
              }`}>
                <div className="flex items-center">
                  <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode ? 'text-green-400' : 'text-green-600'
                  }`} />
                  Handles any email format or structure
                </div>
                <div className="flex items-center">
                  <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode ? 'text-green-400' : 'text-green-600'
                  }`} />
                  Extracts customer details, amounts, and booking info
                </div>
                <div className="flex items-center">
                  <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode ? 'text-green-400' : 'text-green-600'
                  }`} />
                  Manual review and approval workflow
                </div>
                <div className="flex items-center">
                  <CheckCircle className={`w-4 h-4 mr-2 flex-shrink-0 transition-colors duration-300 ${
                    isDarkMode ? 'text-green-400' : 'text-green-600'
                  }`} />
                  Confidence scoring for extracted data
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className={`text-center p-3 rounded-lg transition-colors duration-300 ${
                isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
              }`}>
                <div className={`text-lg font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-zinc-100' : 'text-gray-900'
                }`}>
                  {aiStatsLoading ? (
                    <div className={`animate-spin rounded-full h-5 w-5 border-b-2 mx-auto transition-colors duration-300 ${
                      isDarkMode ? 'border-gray-400' : 'border-gray-600'
                    }`}></div>
                  ) : (
                    aiStats.extracted
                  )}
                </div>
                <div className={`text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                }`}>Pending Review</div>
              </div>
              <div className={`text-center p-3 rounded-lg transition-colors duration-300 ${
                isDarkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50'
              }`}>
                <div className={`text-lg font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-green-300' : 'text-green-600'
                }`}>
                  {aiStatsLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600 mx-auto"></div>
                  ) : (
                    aiStats.promoted
                  )}
                </div>
                <div className={`text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                }`}>Approved</div>
              </div>
              <div className={`text-center p-3 rounded-lg transition-colors duration-300 ${
                isDarkMode ? 'bg-red-900/20 border border-red-700' : 'bg-red-50'
              }`}>
                <div className={`text-lg font-bold transition-colors duration-300 ${
                  isDarkMode ? 'text-red-300' : 'text-red-600'
                }`}>
                  {aiStatsLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600 mx-auto"></div>
                  ) : (
                    aiStats.rejected
                  )}
                </div>
                <div className={`text-xs transition-colors duration-300 ${
                  isDarkMode ? 'text-skyone-muted-dark' : 'text-gray-600'
                }`}>Rejected</div>
              </div>
            </div>

            {/* Action Button */}
            <div className="flex justify-center space-x-3">
              <button
                onClick={fetchAiStats}
                disabled={aiStatsLoading}
                className={`btn flex items-center ${isDarkMode ? 'btn-secondary-dark' : 'btn-secondary'}`}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${aiStatsLoading ? 'animate-spin' : ''}`} />
                Refresh Stats
              </button>
              <Link
                to="/admin/enhanced-email"
                className="btn btn-primary flex items-center"
              >
                <Brain className="w-4 h-4 mr-2" />
                Open AI Dashboard
              </Link>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedEmailManagement;
